import React from "react";
import { Box, Skeleton } from "@mui/material";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { KpiSummary } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface KpiStatsBarProps {
  kpiSummary: KpiSummary | null;
  loading: boolean;
  compact?: boolean;
}

/* ─── Vertical divider between stat groups ─── */
const StatDivider = () => (
  <Box
    sx={{
      width: "1px",
      height: "24px",
      backgroundColor: "#E8E5DF",
      flexShrink: 0,
    }}
  />
);

const KpiStatsBar = ({ kpiSummary, loading, compact }: KpiStatsBarProps) => {
  const { translate } = useTranslation("operations");
  const { isPhone } = useBreakpoint();

  const showSkeleton = loading && !kpiSummary;

  const activeCount = kpiSummary?.activeCount ?? 0;
  const criticalCount = kpiSummary?.criticalCount ?? 0;
  const avgDays = kpiSummary ? (kpiSummary.avgResolutionDays ?? 0).toFixed(1) : "—";

  if (isPhone) {
    /* Phone: compact inline stats — "17 work orders | 4 critical" */
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          opacity: loading ? 0.6 : 1,
          transition: "opacity 0.2s ease",
          minWidth: 0,
        }}
      >
        {showSkeleton ? (
          <>
            <Skeleton variant="rounded" width={100} height={16} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="rounded" width={60} height={16} sx={{ borderRadius: "4px" }} />
          </>
        ) : (
          <>
            {/* Active count + label */}
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", whiteSpace: "nowrap" }}>
              <Box sx={{ fontFamily: "'Sora', sans-serif", fontSize: "14px", fontWeight: 700, color: "#0C1117", lineHeight: 1 }}>
                {activeCount}
              </Box>
              <Box sx={{ fontFamily: "'Sora', sans-serif", fontSize: "11px", fontWeight: 500, color: "#8C9099" }}>
                {translate("kpi.activeOrders")}
              </Box>
            </Box>
            {/* Pipe divider */}
            <Box sx={{ fontFamily: "'Sora', sans-serif", fontSize: "11px", color: "#C1C7CD", flexShrink: 0 }}>|</Box>
            {/* Critical count + label */}
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", whiteSpace: "nowrap" }}>
              <Box sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", fontWeight: 700, color: "#D4572B", lineHeight: 1 }}>
                {criticalCount}
              </Box>
              <Box sx={{ fontFamily: "'Sora', sans-serif", fontSize: "11px", fontWeight: 500, color: "#8C9099" }}>
                {translate("kpi.critical")}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: compact ? "20px" : "32px",
        opacity: loading ? 0.6 : 1,
        transition: "opacity 0.2s ease, gap 0.25s ease",
      }}
    >
      {showSkeleton ? (
        <>
          <Skeleton variant="rounded" width={compact ? 60 : 80} height={compact ? 22 : 28} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="rounded" width={60} height={14} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="rounded" width={80} height={14} sx={{ borderRadius: "4px" }} />
        </>
      ) : (
        <>
          {/* Primary stat: active work orders */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: compact ? "6px" : "8px" }}>
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: compact ? "22px" : "28px",
                fontWeight: 800,
                color: "#0C1117",
                lineHeight: compact ? "22px" : "28px",
                letterSpacing: "-0.03em",
                transition: "font-size 0.25s ease",
              }}
            >
              {activeCount}
            </Box>
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("kpi.activeOrders")}
            </Box>
          </Box>

          <StatDivider />

          {/* Critical count */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: compact ? "5px" : "8px" }}>
            <Box
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: compact ? "13px" : "14px",
                fontWeight: 600,
                color: "#D4572B",
              }}
            >
              {criticalCount}
            </Box>
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: compact ? "11px" : "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("kpi.critical")}
            </Box>
          </Box>

          <StatDivider />

          {/* Avg resolution */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: compact ? "5px" : "8px" }}>
            <Box
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: compact ? "13px" : "14px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {avgDays}{translate("kpi.days")}
            </Box>
            <Box
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontSize: compact ? "11px" : "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("kpi.avgResolution")}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default KpiStatsBar;
